import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase, signUp, signInWithGoogle, createOrganization, generateShortCode } from '../lib/supabase'
import { PLANS, ORG_PRESETS } from '../lib/config'
import { useAuth } from '../hooks/useAuth'

// ── Friendly error sanitizer ──────────────────────────────────────
function friendlyError(err) {
  const msg = (err?.message || err || '').toLowerCase()
  if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('user already exists')) return 'An account with this email already exists. Sign in instead.'
  if (msg.includes('password') && msg.includes('short')) return 'Password must be at least 8 characters.'
  if (msg.includes('invalid email') || msg.includes('email address')) return 'Please enter a valid email address.'
  if (msg.includes('network') || msg.includes('fetch')) return 'Connection problem. Check your internet and try again.'
  if (msg.includes('duplicate') || msg.includes('unique')) return 'Something went wrong with your setup. Please try again.'
  if (msg.includes('row-level') || msg.includes('rls') || msg.includes('policy') || msg.includes('permission') || msg.includes('recursion')) return 'A setup error occurred. Please try again or contact support.'
  if (msg.includes('jwt') || msg.includes('token') || msg.includes('session')) return 'Your session expired. Please refresh and try again.'
  if (msg.includes('timeout') || msg.includes('timed out')) return 'The request took too long. Please try again.'
  if (msg.includes('rate limit') || msg.includes('too many')) return 'Too many attempts. Please wait a moment and try again.'
  return 'Something went wrong. Please try again.'
}

const STEPS = ['Plan', 'Organisation', 'Customise']

export default function Signup() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, org, loading: authLoading } = useAuth()

  const [step, setStep] = useState(0)
  const [pageLoading, setPageLoading] = useState(false)
  const [error, setError] = useState('')

  // Account state — set by Google or manual entry
  const [googleUser, setGoogleUser] = useState(null) // set when coming from Google OAuth
  const [pendingGoogleSetup, setPendingGoogleSetup] = useState(false)

  // Plan + org state
  const [plan, setPlan] = useState(location.state?.plan || 'starter')
  const [orgType, setOrgType] = useState('')
  const [orgName, setOrgName] = useState('')
  const [idMethod, setIdMethod] = useState('name_and_id')
  const [customLabels, setCustomLabels] = useState({})
  const [successData, setSuccessData] = useState(null)

  const preset = ORG_PRESETS.find(p => p.key === orgType)
  const selectedPlan = PLANS.find(p => p.key === plan)
  function getLabel(key) { return customLabels[key] ?? preset?.[key] ?? '' }

  // If already logged in with an org → go to admin
  useEffect(() => {
    if (!authLoading && user && org) navigate('/admin', { replace: true })
  }, [authLoading, user, org, navigate])

  // Check if returning from Google OAuth with no org yet
  useEffect(() => {
    if (!authLoading && user && !org) {
      // User authenticated via Google but hasn't set up org yet
      setGoogleUser(user)
      setPendingGoogleSetup(true)
      // Skip the account step — go straight to plan
      setStep(0)
    }
  }, [authLoading, user, org])

  async function handleGoogleSignup() {
    setError('')
    const { error: err } = await signInWithGoogle()
    if (err) setError(friendlyError(err))
    // On success, Google redirects to /auth/callback which redirects back here
    // The useEffect above will detect user + no org and set pendingGoogleSetup = true
  }

  async function handleFinish() {
    setPageLoading(true); setError('')
    let adminId = null
    let createdAuthUser = false

    try {
      if (pendingGoogleSetup && googleUser) {
        // Google user — already authenticated
        adminId = googleUser.id
      } else {
        // This path shouldn't be reached in the new flow but kept as fallback
        setError('Something went wrong. Please refresh and try again.')
        setPageLoading(false)
        return
      }

      const shortCode = generateShortCode()
      const trialEnd = new Date()
      trialEnd.setDate(trialEnd.getDate() + 14)
      const semesterStart = new Date().toISOString().slice(0, 10)
      const semesterEnd = new Date()
      semesterEnd.setMonth(semesterEnd.getMonth() + 5)

      const orgConfig = {
        admin_id: adminId,
        name: orgName,
        org_type: orgType,
        short_code: shortCode,
        admin_display_name: googleUser?.user_metadata?.full_name || googleUser?.user_metadata?.name || orgName,
        plan,
        id_method: idMethod,
        semester_start: semesterStart,
        semester_end: semesterEnd.toISOString().slice(0, 10),
        subscription_status: 'trial',
        trial_ends_at: trialEnd.toISOString(),
        paystack_ref: null,
        member_label: getLabel('member_label'),
        members_label: getLabel('members_label'),
        id_field_label: getLabel('id_field_label'),
        session_label: getLabel('session_label'),
        sessions_label: getLabel('sessions_label'),
        group_label: getLabel('group_label'),
        groups_label: getLabel('groups_label'),
        location_label: getLabel('location_label'),
        locations_label: getLabel('locations_label'),
        attendance_label: getLabel('attendance_label'),
      }

      const { error: orgErr } = await createOrganization(orgConfig)
      if (orgErr) throw orgErr

      setSuccessData({ shortCode, orgName, plan })
    } catch (e) {
      // If org creation failed and we created an auth user, clean it up
      if (createdAuthUser && adminId) {
        try { await supabase.auth.admin.deleteUser(adminId) } catch {}
      }
      setError(friendlyError(e))
    }
    setPageLoading(false)
  }

  // ── Success screen ────────────────────────────────────────────
  if (successData) {
    return (
      <div className="page-center">
        <div className="container-sm" style={{ width: '100%' }}>
          <div className="card text-center fade-up">
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--green-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 1rem' }}>🎉</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>You're in — 14-day free trial started!</div>
            <div className="text-sm text-muted mb-4">
              Your organisation is live. You have <strong>14 days of full access</strong> on the {selectedPlan?.name} plan. No payment needed until your trial ends.
            </div>
            <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1.25rem', textAlign: 'left' }}>
              <div className="text-xs text-muted mb-2" style={{ fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Your organisation code</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: 'var(--blue)', letterSpacing: 4, marginBottom: 8 }}>{successData.shortCode}</div>
              <div className="text-xs text-muted">Share this with your members so they can mark attendance.</div>
            </div>
            <div className="alert alert-warning mb-4" style={{ textAlign: 'left', fontSize: 13 }}>
              ⏳ <strong>Trial ends in 14 days.</strong> Activate your plan from your admin portal to keep everything running.
            </div>
            <button className="btn btn-primary btn-full" onClick={() => navigate('/admin')}>Go to my admin portal →</button>
          </div>
        </div>
      </div>
    )
  }

  // ── Loading / auth check ──────────────────────────────────────
  if (authLoading) {
    return (
      <div className="page-center">
        <span className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
      </div>
    )
  }

  // ── Google setup flow (user authenticated, no org) ────────────
  if (pendingGoogleSetup) {
    const userName = googleUser?.user_metadata?.full_name || googleUser?.user_metadata?.name || 'there'
    return (
      <div className="page-center">
        <div className="container-sm" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div className="logo" style={{ marginBottom: 4 }}>Campus<span>Flow</span></div>
            <p className="text-sm text-muted">Welcome, {userName} — finish setting up your organisation</p>
          </div>

          <div className="steps mb-4">
            {STEPS.map((s, i) => (
              <div key={s} className="step-item">
                <div className={`step-dot ${i < step ? 'done' : i === step ? 'current' : 'pending'}`}>{i < step ? '✓' : i + 1}</div>
                <div className={`step-label ${i === step ? 'current' : ''}`}>{s}</div>
                {i < STEPS.length - 1 && <div className="step-line" />}
              </div>
            ))}
          </div>

          {error && <div className="alert alert-error mb-3">{error}</div>}

          {/* Step 0: Plan */}
          {step === 0 && (
            <div className="card fade-up">
              <div className="card-title mb-1">Choose your plan</div>
              <div className="card-sub mb-3">Try any plan free for 14 days. Pay only when your trial ends.</div>
              <div style={{ display: 'grid', gap: 10, marginBottom: '1.25rem' }}>
                {PLANS.map(p => (
                  <div key={p.key} onClick={() => setPlan(p.key)} style={{
                    border: `2px solid ${plan === p.key ? 'var(--blue)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', padding: '12px 14px', cursor: 'pointer',
                    background: plan === p.key ? 'var(--blue-light)' : 'var(--bg)',
                  }}>
                    <div className="flex justify-between items-center mb-1">
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {p.name}
                        {p.popular && <span className="badge badge-blue" style={{ marginLeft: 6 }}>Popular</span>}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: plan === p.key ? 'var(--blue)' : 'var(--text)' }}>
                        GH₵{p.price}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>/year</span>
                      </div>
                    </div>
                    <div className="text-xs text-muted">{p.features.slice(0, 3).join(' · ')}</div>
                  </div>
                ))}
              </div>
              <button className="btn btn-primary btn-full" onClick={() => setStep(1)}>
                Continue with {selectedPlan?.name} →
              </button>
            </div>
          )}

          {/* Step 1: Org setup */}
          {step === 1 && (
            <div className="card fade-up">
              <div className="card-title mb-1">Set up your organisation</div>
              <div className="card-sub mb-3">This personalises all labels throughout your portal</div>
              <div style={{ display: 'grid', gap: 8, marginBottom: '1rem' }}>
                {ORG_PRESETS.map(p => (
                  <div key={p.key} onClick={() => setOrgType(p.key)} style={{
                    border: `2px solid ${orgType === p.key ? 'var(--blue)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', padding: '10px 14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: orgType === p.key ? 'var(--blue-light)' : 'var(--bg)',
                  }}>
                    <span style={{ fontSize: 20 }}>{p.icon}</span>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{p.label}</div>
                      {orgType === p.key && <div className="text-xs text-muted">{p.member_label} · {p.id_field_label} · {p.session_label}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="field-label">Organisation name</label>
                <input type="text" placeholder="e.g. Koforidua Technical University" value={orgName} onChange={e => setOrgName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="field-label">How do members identify themselves?</label>
                <select value={idMethod} onChange={e => setIdMethod(e.target.value)}>
                  <option value="name_and_id">Name + ID (name first, then ID to confirm)</option>
                  <option value="id_only">ID only (member types ID directly)</option>
                </select>
                <div className="field-hint">You can change this later in Settings.</div>
              </div>
              <div className="flex gap-2 mt-3">
                <button className="btn btn-secondary" onClick={() => setStep(0)}>← Back</button>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={!orgType || !orgName} onClick={() => setStep(2)}>Continue →</button>
              </div>
            </div>
          )}

          {/* Step 2: Customise */}
          {step === 2 && preset && (
            <div className="card fade-up">
              <div className="card-title mb-1">Customise your labels</div>
              <div className="card-sub mb-3">Pre-filled for your org type. Change anything that doesn't fit.</div>
              {[
                { key: 'member_label', label: 'What do you call one person?' },
                { key: 'id_field_label', label: 'What is the unique ID field called?' },
                { key: 'group_label', label: 'What do you call a group / class?' },
                { key: 'session_label', label: 'What do you call a session?' },
                { key: 'location_label', label: 'What do you call a venue?' },
              ].map(f => (
                <div key={f.key} className="form-group">
                  <label className="field-label">{f.label}</label>
                  <input type="text" value={customLabels[f.key] ?? preset[f.key]} onChange={e => setCustomLabels({ ...customLabels, [f.key]: e.target.value })} />
                </div>
              ))}
              <div className="flex gap-2 mt-3">
                <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={pageLoading} onClick={handleFinish}>
                  {pageLoading ? <><span className="spinner" /> Setting up...</> : '🚀 Start my free trial'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Default: Google sign-in entry point ───────────────────────
  return (
    <div className="page-center">
      <div className="container-sm" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="logo" style={{ marginBottom: 4 }}>Campus<span>Flow</span></div>
          <p className="text-sm text-muted">14-day free trial · No payment required</p>
        </div>

        {error && <div className="alert alert-error mb-3">{error}</div>}

        <div className="card fade-up">
          <div className="card-title mb-1">Get started with CampusFlow</div>
          <div className="card-sub mb-4">Create your admin account to set up attendance tracking for your organisation.</div>

          <button className="btn btn-google mb-3" onClick={handleGoogleSignup}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: 18, height: 18 }}>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span className="text-xs text-muted">already have an account?</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <button className="btn btn-secondary btn-full" onClick={() => navigate('/login')}>Sign in</button>

          <p className="text-xs text-muted text-center mt-3" style={{ lineHeight: 1.6 }}>
            By continuing you agree to our terms of service. Your data is stored securely and never sold.
          </p>
        </div>
      </div>
    </div>
  )
}
